import { ClipboardList, Code, LineChart, ArrowRight, CheckCircle2, Zap, Sparkles } from 'lucide-react'

const steps = [
  {
    id: 1,
    name: 'Crea tu MVP y registralo en Validea',
    description: 'Configurá tu proyecto y definí las características que querés validar.',
    icon: ClipboardList,
    details: [
      'Registro rápido y sencillo',
      'Configuración de características a validar',
      'Personalización de métricas',
      'Definición de objetivos'
    ],
    benefits: [
      'Estructura clara de tu MVP',
      'Foco en lo importante',
      'Métricas alineadas'
    ],
    color: 'from-blue-500 to-blue-600'
  },
  {
    id: 2,
    name: 'Integrá nuestro snippet de tracking y formulario',
    description: 'Agregá nuestro código a tu landing page en segundos.',
    icon: Code,
    details: [
      'Código de una sola línea',
      'Formularios pre-diseñados',
      'Integración con tu stack',
      'Configuración en minutos'
    ],
    benefits: [
      'Implementación rápida',
      'Sin desarrollo complejo',
      'Compatible con cualquier sitio'
    ],
    color: 'from-purple-500 to-purple-600'
  },
  {
    id: 3,
    name: 'Observá el interés real y tomá decisiones',
    description: 'Analizá los datos y descubrí qué funcionalidades generan más interés.',
    icon: LineChart,
    details: [
      'Dashboard en tiempo real',
      'Métricas de engagement',
      'Análisis de comportamiento',
      'Reportes automáticos'
    ],
    benefits: [
      'Decisiones basadas en datos',
      'Validación objetiva',
      'Optimización continua'
    ],
    color: 'from-green-500 to-green-600'
  }
]

/**
 * HowItWorks section component for the landing page
 * Displays a step-by-step guide on how to use the product
 */
export function HowItWorks() {
  return (
    <section id="howitworks" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-base font-semibold leading-7 text-primary-600">Proceso Simple</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Tres pasos simples para empezar a validar tu idea
          </p>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            Comenzá a validar tu idea en minutos, sin complicaciones ni desarrollo complejo.
          </p>
        </div>

        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
            {steps.map((step, index) => (
              <div key={step.name} className="relative">
                {/* Step number and connector line */}
                <div className="absolute -left-4 top-0 flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white">
                  {step.id}
                </div>
                {index < steps.length - 1 && (
                  <div className="absolute left-3 top-8 h-full w-0.5 bg-gray-200" />
                )}

                <div className="relative pl-12">
                  <div className="group relative flex flex-col rounded-2xl border border-gray-200 bg-white p-8 shadow-sm transition-all hover:shadow-lg">
                    <div className="flex items-center gap-x-4">
                      <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r ${step.color}`}>
                        <step.icon className="h-6 w-6 text-white" aria-hidden="true" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold leading-7 text-gray-900">
                          {step.name}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 flex-1">
                      <div className="relative">
                        <div className="absolute -left-4 top-0 h-full w-0.5 bg-gradient-to-b from-primary-500 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                        <ul role="list" className="space-y-3">
                          {step.details.map((detail) => (
                            <li key={detail} className="flex items-start gap-x-3">
                              <CheckCircle2 className="mt-1 h-4 w-4 flex-none text-primary-600" />
                              <span className="text-sm text-gray-600">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-6">
                      <div className="inline-flex items-center gap-x-2 rounded-full bg-primary-50 px-3 py-1 text-sm font-medium text-primary-600">
                        <Sparkles className="h-4 w-4" />
                        <span>Beneficios clave</span>
                      </div>
                      <ul className="mt-3 space-y-2">
                        {step.benefits.map((benefit) => (
                          <li key={benefit} className="flex items-center gap-x-2 text-sm text-gray-600">
                            <Zap className="h-4 w-4 text-primary-500" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {index < steps.length - 1 && (
                      <div className="absolute -right-4 top-1/2 -translate-y-1/2">
                        <ArrowRight className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </dl>
        </div>

        <div className="mt-16 text-center">
          <a
            href="#contact"
            className="inline-flex items-center gap-x-2 rounded-full bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
          >
            Comenzá ahora
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  )
} 